import {
    Box,
    Checkbox,
    FormControlLabel,
    Stack,
    Tab,
    Tabs,
    type Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import HybridCard, { type Hybrid } from './hybrid-card';
import { filterSilo, groupByBrand, hasSiloChoice, rank } from './hybrid-lists';
import { useFieldRisks } from './use-field-risks';
import { useHybrids } from './use-hybrids';
import { usePreselectedProduct } from './use-preselected-product';

/** The catalogue tab; every other tab key is a brand bucket built by `groupByBrand`. */
const CATALOG_TAB = 'catalog';

/**
 * "Híbridos recomendados" step of the seed recommendation wizard.
 *
 * The list is keyed on the crop config the previous steps built, so it re-ranks whenever the
 * environment changes, and selection is written back to `cropConfig.products` — the shape the
 * recommendation-edit page already saves.
 */
function HybridsStep({ field, crop = 'corn' }: { field: Field; crop?: 'corn' | 'soy' }) {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const [tab, setTab] = useState<string | null>(null);
    const [siloOnly, setSiloOnly] = useState(false);

    const handleChange = (key: string) => {
        return async (value: unknown) => {
            await dispatch(updateActiveRecommendationCropConfig({ key, value }));
        };
    };

    const { featuredProducts, otherProducts, brands, isLoading } = useHybrids({ field, crop });
    const { risks } = useFieldRisks(field);

    const groups = useMemo(() => groupByBrand(featuredProducts, brands), [featuredProducts, brands]);

    usePreselectedProduct({
        offered: useMemo(() => [...featuredProducts, ...otherProducts], [featuredProducts, otherProducts]),
        selectedIds: cropConfig?.products || [],
    });

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    // Nothing is picked until the brands are known, so the first tab opens by default — and
    // since the brands the ranking was asked for always have a group, a brand the user picked
    // stays picked across an environment change even when it loses all of its hybrids; the empty
    // state then names it, rather than silently moving the user to another brand's list.
    const isKnownTab = tab === CATALOG_TAB || groups.some((group) => group.key === tab);
    const activeTab = tab && isKnownTab ? tab : (groups[0]?.key ?? CATALOG_TAB);
    const isCatalog = activeTab === CATALOG_TAB;

    const selectedIds: number[] = cropConfig.products || [];
    const maxSelection = settings.maxProductSelection;
    const atLimit = selectedIds.length >= maxSelection;

    const toggle = (id: number) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter((selectedId) => selectedId !== id)
            : [...selectedIds, id];
        if (next.length > maxSelection) return;
        handleChange('products')(next);
    };

    // The tab's full list decides whether the silo filter is worth offering; the filter then
    // narrows what is shown. Brand tabs re-number so a filtered list still reads 1, 2, 3 — the
    // catalogue does not, its ranks being incidental to an alphabetical sort.
    const activeGroup = groups.find((group) => group.key === activeTab);
    const tabHybrids: Hybrid[] = isCatalog ? otherProducts : (activeGroup?.hybrids ?? []);
    const filtered = filterSilo(tabHybrids, { siloOnly, keepIds: selectedIds });
    const hybrids: Hybrid[] = isCatalog ? filtered : rank(filtered);
    const showSiloFilter = hasSiloChoice(tabHybrids);

    // A brand the ranking came back empty for is named, so the user knows it is that brand and
    // not the app that has nothing to offer here. A tab the silo filter emptied, and the
    // catalogue, keep the generic advice: there the environment is still the thing to change.
    const emptyMessage =
        !isCatalog && activeGroup?.name && !tabHybrids.length
            ? _('No hay híbridos {{brand}} recomendados para este lote y ambiente.', {
                  brand: activeGroup.name,
              })
            : _('No encontramos híbridos para este ambiente. Probá ajustando el ambiente del lote.');

    return (
        <Stack spacing={3} pb={4}>
            <Tabs
                value={activeTab}
                onChange={(_event, value) => setTab(value)}
                // One tab per brand plus the catalogue overflows a phone at full width.
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                scrollButtons={false}
                textColor='secondary'
                indicatorColor='secondary'
            >
                {groups.map((group) => (
                    <Tab key={group.key} value={group.key} label={group.label} />
                ))}
                <Tab value={CATALOG_TAB} label={isMobile ? _('Otros híbridos') : _('Otros híbridos del catálogo')} />
            </Tabs>

            {showSiloFilter && (
                <FormControlLabel
                    control={<Checkbox checked={siloOnly} onChange={(_event, checked) => setSiloOnly(checked)} />}
                    label={<Typography variant='body2'>{_('Mostrar híbridos para silo')}</Typography>}
                    sx={{ alignSelf: 'flex-start', mr: 0 }}
                />
            )}

            {isLoading && !hybrids.length ? (
                <Stack alignItems='center' py={6}>
                    <Spinner />
                </Stack>
            ) : hybrids.length === 0 ? (
                <Alert severity='surfaceVariant'>{emptyMessage}</Alert>
            ) : (
                <Box
                    display='grid'
                    gap={2}
                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                    alignItems='stretch'
                >
                    {hybrids.map((hybrid) => {
                        const selected = selectedIds.includes(hybrid.id);
                        return (
                            <HybridCard
                                key={hybrid.id}
                                hybrid={hybrid}
                                risks={risks}
                                selected={selected}
                                onToggle={() => toggle(hybrid.id)}
                                disabled={!selected && atLimit}
                                compact={isMobile}
                                showRank={!isCatalog || isMobile}
                                showYield={!isCatalog}
                            />
                        );
                    })}
                </Box>
            )}

            {atLimit && (
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Seleccionaste el máximo de {{count}} híbridos. Deseleccioná uno para elegir otro.', {
                        count: maxSelection,
                    })}
                </Typography>
            )}
        </Stack>
    );
}

export default HybridsStep;
